/**
 * Store de Pinia para gestión de alertas y notificaciones globales
 *
 * Este store centraliza el manejo de todas las notificaciones de la aplicación,
 * permitiendo que cualquier componente o store pueda mostrar alertas de
 * manera consistente.
 *
 * FUNCIONALIDADES:
 * - Mostrar alertas con diferentes niveles (éxito, error, información)
 * - Auto-cierre de alertas después de un tiempo configurable
 * - Cola de alertas para manejar múltiples notificaciones
 * - API simple para mostrar y ocultar alertas
 */

import { defineStore } from 'pinia'
import { ref, readonly } from 'vue'

export const useAlertsStore = defineStore('alerts', () => {
  // =============================================
  // ESTADO (STATE)
  // =============================================

  /**
   * Array reactivo que contiene las alertas activas
   * @type {Array<Object>}
   */
  const alerts = ref([])

  /**
   * Contador para generar IDs únicos para cada alerta
   * @type {number}
   */
  let nextId = 1

  // =============================================
  // ACCIONES (ACTIONS)
  // =============================================

  /**
   * Muestra una nueva alerta en la UI
   *
   * @param {string} message - El mensaje a mostrar en la alerta
   * @param {string} [type='info'] - El tipo de alerta ('success', 'error', 'info', 'warning')
   * @param {number} [duration=4000] - Duración en ms. Si es 0, la alerta es persistente.
   * @returns {number} El ID de la alerta creada
   */
  const showAlert = (message, type = 'info', duration = 4000) => {
    const id = nextId++

    const newAlert = {
      id,
      message,
      type,
      duration,
      visible: true // Para animaciones de entrada/salida
    }

    // Agregar la nueva alerta al principio del array
    alerts.value.unshift(newAlert)

    // Si la duración es mayor a 0, programar su auto-cierre
    if (duration > 0) {
      setTimeout(() => {
        removeAlert(id)
      }, duration)
    }

    console.log(`🔔 Alerta #${id} mostrada: "${message}" (Tipo: ${type})`)

    return id
  }

  /**
   * Oculta y elimina una alerta por su ID
   *
   * @param {number} id - El ID de la alerta a eliminar
   */
  const removeAlert = (id) => {
    const index = alerts.value.findIndex(alert => alert.id === id)

    if (index !== -1) {
      // Marcar como no visible para animaciones de salida
      alerts.value[index].visible = false

      // Eliminar del array después de la animación (ej: 300ms)
      setTimeout(() => {
        const finalIndex = alerts.value.findIndex(alert => alert.id === id)
        if (finalIndex !== -1) {
          alerts.value.splice(finalIndex, 1)
          console.log(`🔕 Alerta #${id} eliminada.`)
        }
      }, 300)
    }
  }

  /**
   * Limpia todas las alertas visibles
   */
  const clearAllAlerts = () => {
    alerts.value = []
    console.log('🧹 Todas las alertas han sido eliminadas.')
  }

  // =============================================
  // API RETORNO DEL STORE
  // =============================================
  return {
    // Estado (solo lectura para componentes)
    alerts: readonly(alerts),

    // Acciones
    showAlert,
    removeAlert,
    clearAllAlerts
  }
})
